// @flow

import {
  webPagePath,
  registrationPagePath,
  sendResetPasswordEmailPath
} from 'WebModule/app/routerPaths';
import FocusBody from 'components/FocusBody';
import Page from 'components/Page';
import * as React from 'react';
import gql from 'graphql-tag';
import { Mutation } from 'react-apollo';
import { path } from 'rambda';
import logoBlackWide from 'themes/images/logo-black-wide.png';
import jwtDecode from 'jwt-decode';
import { withCookies } from 'react-cookie';
import Text from 'components/Text';
import View from 'components/View';
import FocusContent from 'components/FocusContent';
import LoginBottomBar from 'components/LoginBottomBar';
import { DetailBottomBarConfirm } from 'components/DetailBottomBarItem';
import FormRow from 'components/FormRow';
import TextInput from 'components/TextInput';
import { NotificationBar } from 'components/DetailBottomBar';
import { injectIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { generatePath } from 'react-router';
import FocusHeader from '../components/FocusHeader';
import {
  AUTH_TOKEN,
  cookiesSetting,
  USERNAME,
  USER_ID,
  LOGOUT_MESSAGE
} from '../../common/constants';
import { getAllowLanguageSwitch, storeLanguage } from '../../utils/i18n';
import withLocaleContext from '../../components/hoc/withLocaleContext';

const styles = {
  page: (props, { components: { FocusPage } }) => ({
    ...FocusPage.page,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '-100px'
  }),
  text: (props, { components: { FocusPage } }) => ({
    ...FocusPage.text
  }),
  img: {
    marginLeft: 20,
    height: 20
  },
  error: (props, { components: { Login } }) => ({
    ...Login.error
  }),
  registrationLink: {
    height: '60px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  registrationLinkText: (props, { colors }) => ({
    color: colors[0][0],

    '&:hover': {
      color: colors[0][20]
    }
  }),
  forgotPasswordLink: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    boxSizing: 'border-box',
    paddingTop: '6px'
  },
  forgotPasswordLinkText: (props, { colors }) => ({
    '&:hover': {
      color: colors[0][30]
    }
  })
};

const loginQuery = gql`
  mutation LoginMutation($name: String!, $password: String!) {
    tokenAuth(username: $name, password: $password) {
      token
    }
  }
`;

const LoginButton = withLocaleContext(
  withCookies(
    ({
      cookies,
      history,
      location,
      name,
      password,
      intl,
      wasEnterHit,
      cleanEnterState,
      localeContext
    }) => {
      const { changeLocale } = localeContext;

      return (
        <Mutation
          mutation={loginQuery}
          variables={{ name, password }}
          onCompleted={data => {
            cookies.remove(LOGOUT_MESSAGE);
            if (path('tokenAuth.token', data)) {
              const tokenPayload = jwtDecode(data.tokenAuth.token);

              cookies.set(AUTH_TOKEN, data.tokenAuth.token, cookiesSetting);
              cookies.set(USER_ID, tokenPayload.user_id, cookiesSetting);
              cookies.set(
                USERNAME,
                tokenPayload.username || intl.formatMessage({ id: 'login.logged-in' }),
                cookiesSetting
              );

              const receivedLanguage =
                tokenPayload.user_default_language || tokenPayload.company_default_language;

              if (receivedLanguage) {
                storeLanguage(cookies, receivedLanguage);
                changeLocale(receivedLanguage);
              }

              const redirectPathname = path('state.from.pathname', location);

              history.push(redirectPathname || webPagePath);
            }
          }}>
          {(login, { error, loading }) => {
            if (loading)
              return (
                <DetailBottomBarConfirm name={intl.formatMessage({ id: 'login.logging-in' })} />
              );
            if (error)
              return (
                <>
                  <Text style={styles.error}>{intl.formatMessage({ id: 'login.error' })}</Text>
                  <DetailBottomBarConfirm
                    name={intl.formatMessage({ id: 'login.log-in' })}
                    onClick={login}
                  />
                </>
              );

            if (wasEnterHit) {
              login();
              cleanEnterState();
            }

            return (
              <DetailBottomBarConfirm
                name={intl.formatMessage({ id: 'login.log-in' })}
                onClick={login}
              />
            );
          }}
        </Mutation>
      );
    }
  )
);

const RegistrationLink = ({ intl }) => (
  <View style={styles.registrationLink}>
    <Link to={generatePath(registrationPagePath)}>
      <Text style={styles.registrationLinkText}>
        {intl.formatMessage({ id: 'login.new-registration' })}
      </Text>
    </Link>
  </View>
);

const ForgotPasswordLink = ({ intl }) => (
  <View style={styles.forgotPasswordLink}>
    <Link to={generatePath(sendResetPasswordEmailPath)}>
      <Text style={styles.forgotPasswordLinkText}>
        {intl.formatMessage({ id: 'login.forgot-password' })}
      </Text>
    </Link>
  </View>
);

class LoginPage extends React.Component {
  constructor(props) {
    super(props);
    this.enterPress = this.enterPress.bind(this);
    this.cleanEnterState = this.cleanEnterState.bind(this);
  }

  state = {
    name: '',
    password: '',
    logoutMessage: undefined,
    wasEnterHit: undefined
  };

  componentDidMount() {
    const { cookies } = this.props;

    this.usernameInput.focus();
    document.addEventListener('keydown', this.enterPress, false);

    const logoutNotification = cookies.get(LOGOUT_MESSAGE);
    if (logoutNotification) {
      this.setState({ logoutMessage: logoutNotification });
      setTimeout(() => {
        cookies.remove(LOGOUT_MESSAGE);
      }, 4000);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.enterPress, false);
  }

  updateParameter = (parameter, e) => {
    const { [parameter]: param } = this.state;
    if (e.target.value !== param) {
      this.setState({ [parameter]: e.target.value });
    }
  };

  enterPress(event) {
    if (event.key === 'Enter') {
      this.setState({ wasEnterHit: true });
    } else {
      this.setState({ wasEnterHit: false });
    }
  }

  cleanEnterState() {
    this.setState({ wasEnterHit: false });
  }

  render() {
    const { history, location, cookies, intl } = this.props;
    const { name, password, logoutMessage, wasEnterHit } = this.state;

    return (
      <Page title={intl.formatMessage({ id: 'login.login' })}>
        <View isExpanded style={styles.page}>
          <View style={{ width: 500 }}>
            <FocusContent>
              <FocusHeader
                title={intl.formatMessage({ id: 'login.alt-title' })}
                subTitle={intl.formatMessage({ id: 'login.subtext' })}
                languageSwitcher={getAllowLanguageSwitch(cookies)}
              />
              <FocusBody>
                <View isExpanded isVertical style={{ padding: 40, paddingBottom: 20 }}>
                  <FormRow>
                    <TextInput
                      label={intl.formatMessage({ id: 'login.username' })}
                      nativeProps={{
                        ref: c => {
                          this.usernameInput = c;
                        },
                        value: name,
                        onChange: e => this.updateParameter('name', e)
                      }}
                    />
                  </FormRow>

                  <FormRow>
                    <TextInput
                      label={intl.formatMessage({ id: 'login.password' })}
                      nativeProps={{
                        type: 'password',
                        value: password,
                        onChange: e => this.updateParameter('password', e)
                      }}
                    />
                  </FormRow>

                  <ForgotPasswordLink intl={intl} />
                </View>

                <LoginBottomBar>
                  <img src={logoBlackWide} alt="LoyalMAN" style={styles.img} />

                  <LoginButton
                    history={history}
                    location={location}
                    name={name}
                    password={password}
                    intl={intl}
                    wasEnterHit={wasEnterHit}
                    cleanEnterState={this.cleanEnterState}
                  />
                </LoginBottomBar>
              </FocusBody>
              {logoutMessage && (
                <NotificationBar
                  text={logoutMessage}
                  clearFn={() => {
                    cookies.remove(LOGOUT_MESSAGE);
                    this.setState({ logoutMessage: undefined });
                  }}
                />
              )}
              <RegistrationLink intl={intl} />
            </FocusContent>
          </View>
        </View>
      </Page>
    );
  }
}

export default injectIntl(withCookies(LoginPage));
